import { useCallback, useEffect, useRef, useState } from 'react';

type Slot = 'filters' | 'sort' | 'search' | 'period' | 'region' | 'status-tab';

export const buildKey = (tableId: string, slot: Slot, corporationId: number) =>
  `indirect-tax-${tableId}-${slot}-corporation-${corporationId}-v1`;

export const load = <T>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (e) {
    console.warn(`Failed to read ${key} from localStorage:`, e);
    return fallback;
  }
};

export const save = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to write ${key} to localStorage:`, e);
  }
};

const sessionLoad = <T>(key: string, fallback: T): T => {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch (e) {
    console.warn(`Failed to read ${key} from sessionStorage:`, e);
    return fallback;
  }
};

const sessionSave = <T>(key: string, value: T): void => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Failed to write ${key} to sessionStorage:`, e);
  }
};

// Pass `key === null` to get plain useState behavior: no read on mount, no
// write on change. Used by the transactions dashboard when invoked from a
// modal with a seeded initialState — those sessions must not leak into or
// out of the corporation-level persisted filters.
//
// Reads happen in a post-mount useEffect, not in a useState lazy initializer,
// because this hook is used inside Next.js pages that server-render. A lazy
// initializer runs on the server where `localStorage` does not exist, returns
// the fallback, and React then reuses that server state on client hydration
// instead of re-running the initializer — so the stored value would never be
// read. The useEffect runs client-only after hydration, at the cost of a
// one-frame render with `initial` before the stored value takes over.
export function usePersistedState<T>(key: string | null, initial: T) {
  // Lock in the first `initial` so callers that recompute it each render
  // (e.g. `initialState?.filters ?? {}`) do not retrigger the hydrating
  // effect below when the object identity of `initial` changes.
  const initialRef = useRef(initial);
  const [value, setValue] = useState<T>(initial);

  useEffect(() => {
    if (key === null) return;
    setValue(load(key, initialRef.current));
  }, [key]);

  const set = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: T) => T)(prev)
            : updater;
        if (key !== null) save(key, next);
        return next;
      });
    },
    [key]
  );
  return [value, set] as const;
}

// Same shape and SSR contract as `usePersistedState`, but backed by
// `sessionStorage` instead of `localStorage`. Use this when the persisted
// value should be tab-scoped and cleared at tab close (e.g. parity with the
// old sales-tax UI's per-tab filter persistence).
//
// Why not `usehooks-ts`'s `useSessionStorage`? It runs `readValue()` inside
// `useState`'s lazy initializer (causing SSR/client hydration mismatches),
// re-reads on every `[key]` change, and dispatches a synthetic
// `'session-storage'` event that all instances re-read on. Together those
// produce the typing-flicker and back-button reset we hit on the customers
// and products dashboards. This hook intentionally avoids all three: single
// post-mount `useEffect` keyed on `[key]`, no synthetic events, no listener.
export function useSessionPersistedState<T>(key: string | null, initial: T) {
  const initialRef = useRef(initial);
  const [value, setValue] = useState<T>(initial);

  useEffect(() => {
    if (key === null) return;
    setValue(sessionLoad(key, initialRef.current));
  }, [key]);

  const set = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: T) => T)(prev)
            : updater;
        if (key !== null) sessionSave(key, next);
        return next;
      });
    },
    [key]
  );
  return [value, set] as const;
}

const EMPTY = Object.freeze({});

export function useRegionScopedSessionState<T>(
  key: string | null,
  region: string,
  seedMap?: Record<string, T>
) {
  const [map, setMap] = useSessionPersistedState<Record<string, T>>(
    key,
    seedMap ?? {}
  );

  const value = (map[region] ?? EMPTY) as T;

  const regionRef = useRef(region);
  regionRef.current = region;

  const setValue = useCallback(
    (updater: T | ((prev: T) => T)) => {
      setMap((prev) => {
        const r = regionRef.current;
        const current = (prev[r] ?? EMPTY) as T;
        const next =
          typeof updater === 'function'
            ? (updater as (p: T) => T)(current)
            : updater;
        return { ...prev, [r]: next };
      });
    },
    [setMap]
  );

  return [value, setValue] as const;
}
